const axios = require('axios');
const { ethers } = require('ethers');
const { stakehouseContracts } = require('@blockswaplab/stakehouse-protocol-abis');
const { 
    _add0x,
    _remove0x,
    _getValidatorLifecycleStatus,
    _calculateExitFee,
    _rageQuitChecks,
    _isBalanceLower,
    _getPersonalSignInitialsByPrivateKey, 
    _getPersonalSignInitials} = require('./utils.js');
const {
    _getChainSpecificConstants, _extractChainID, customErrors, lifecycleStatuses
} = require('./constants.js');
const { getContractInstance } = require('./contracts');

const _reportBalanceIncrease = async (signer, stakeHouseAddress, balanceReport) => {

    const status = await _getValidatorLifecycleStatus(signer, balanceReport.report.blsPublicKey);
    if(status !== lifecycleStatuses.DEPOSIT_COMPLETED && status !== lifecycleStatuses.TOKENS_MINTED) {
        throw customErrors.STATUS_NOT_2_OR_3;
    }

    try {
        const contract = (await getContractInstance(signer)).transactionRouter();

        balanceReport.report.blsPublicKey = _add0x(balanceReport.report.blsPublicKey);
        balanceReport.report.withdrawalCredentials = _add0x(balanceReport.report.withdrawalCredentials);

        const signature = {
            deadline: balanceReport.deadline,
            v: balanceReport.v,
            r: Buffer.from(balanceReport.r, 'hex'),
            s: Buffer.from(balanceReport.s, 'hex'),
        };

        return contract.balanceIncrease(
            stakeHouseAddress,
            balanceReport.report.blsPublicKey,
            balanceReport.report,
            signature
        );
    } catch(e) {
        throw e;
    }
};

const _reportBalance = async (signer, beaconNodeURL, blsPublicKey, stakehouseAddress) => {

    const report = await _isBalanceLower(signer, beaconNodeURL, blsPublicKey);

    if(report) {
        if(report.result) {
            return _slash(
                signer,
                stakehouseAddress,
                report.report
            );
        }
        else {
            return _reportBalanceIncrease(
                signer,
                stakehouseAddress,
                report.report
            );
        }
    }
};

const _registerValidatorInitials = async (signer, userAddress, blsPublicKey, blsSignature) => {

    const status = await _getValidatorLifecycleStatus(signer, blsPublicKey);
    if(status !== lifecycleStatuses.UNBEGUN) {
        throw customErrors.STATUS_NOT_0;
    }

    userAddress = _add0x(userAddress);
    blsPublicKey = _add0x(blsPublicKey);
    blsSignature = _add0x(blsSignature);

    const contract =  (await getContractInstance(signer)).transactionRouter();

    return contract.registerValidatorInitials(
        userAddress,
        blsPublicKey,
        blsSignature
    );
};

const _registerValidator = async (signer, userAddress, authenticationPackage) => {

    const status = await _getValidatorLifecycleStatus(signer, authenticationPackage.deposit_data[0].pubkey);
    if(status !== lifecycleStatuses.INITIALS_REGISTERED) {
        throw customErrors.STATUS_NOT_1;
    }

    const { deposit_data, cipher_text, encryptor_aes_key, deadline, v, r, s } = authenticationPackage;
    const contract =  (await getContractInstance(signer)).transactionRouter();

    const signature = { deadline, v, r: _add0x(r), s: _add0x(s) };

    return contract.registerValidator(
        _add0x(userAddress),
        _add0x(deposit_data[0].pubkey),
        _add0x(cipher_text),
        _add0x(encryptor_aes_key),
        signature,
        _add0x(deposit_data[0].deposit_data_root),
        { value: ethers.utils.parseUnits('32', 'ether') }
    );
};

const _createStakehouse = async (signer, userAddress, ticker, savETHIndexId, balanceReport) => {
    
    const status = await _getValidatorLifecycleStatus(signer, balanceReport.report.blsPublicKey);
    if(status !== lifecycleStatuses.DEPOSIT_COMPLETED) {
        throw customErrors.STATUS_NOT_2;
    }

    const activeBalance = ethers.BigNumber.from(balanceReport.report.activeBalance);
    const threshold = ethers.utils.parseUnits('32', 'gwei');

    if(activeBalance.lt(threshold)) { 
        throw customErrors.ETH_BALANCE_LT_32;
    }

    userAddress = _add0x(userAddress);
    balanceReport.report.blsPublicKey = _add0x(balanceReport.report.blsPublicKey);
    balanceReport.report.withdrawalCredentials = _add0x(balanceReport.report.withdrawalCredentials);

    const contract =  (await getContractInstance(signer)).transactionRouter();

    const signature = {
        deadline: balanceReport.deadline,
        v: balanceReport.v,
        r: Buffer.from(balanceReport.r, 'hex'),
        s: Buffer.from(balanceReport.s, 'hex'),
    };

    return contract.createStakehouse(
        userAddress,
        balanceReport.report.blsPublicKey,
        ticker,
        savETHIndexId,
        balanceReport.report,
        signature
    );
};

const _joinStakehouse = async (signer, userAddress, stakeHouseAddress, brandTokenId, savETHIndexId, balanceReport) => {

    const status = await _getValidatorLifecycleStatus(signer, balanceReport.report.blsPublicKey);
    if(status !== lifecycleStatuses.DEPOSIT_COMPLETED) {
        throw customErrors.STATUS_NOT_2;
    }

    const activeBalance = ethers.BigNumber.from(balanceReport.report.activeBalance);
    const threshold = ethers.BigNumber.from('32000000000');

    if(activeBalance.lt(threshold)) { 
        throw customErrors.ETH_BALANCE_LT_32;
    }

    userAddress = _add0x(userAddress);
    stakeHouseAddress = _add0x(stakeHouseAddress);
    balanceReport.report.blsPublicKey = _add0x(balanceReport.report.blsPublicKey);
    balanceReport.report.withdrawalCredentials = _add0x(balanceReport.report.withdrawalCredentials);

    const contract =  (await getContractInstance(signer)).transactionRouter();

    const signature = {
        deadline: balanceReport.deadline,
        v: balanceReport.v,
        r: Buffer.from(balanceReport.r, 'hex'),
        s: Buffer.from(balanceReport.s, 'hex'),
    };

    return contract.joinStakehouse(
        userAddress,
        balanceReport.report.blsPublicKey,
        stakeHouseAddress,
        brandTokenId,
        savETHIndexId,
        balanceReport.report,
        signature
    );
};

const _addKnotToOpenIndexAndWithdraw = async (signer, stakehouseAddress, memberID, recipientAddress) => {

    memberID = _add0x(memberID);

    const contract =  (await getContractInstance(signer)).savETHManager();

    return contract.addKnotToOpenIndexAndWithdraw(
        stakehouseAddress,
        memberID,
        recipientAddress
    );
};

const _batchAddKnotToOpenIndexAndWithdraw = async (signer, stakehouses, memberIDs, recipient) => {
    
    for(let i=0; i<memberIDs.length; i++) {
        memberIDs[i] = _add0x(memberIDs[i]);
    }

    const contract =  (await getContractInstance(signer)).savETHBatch();

    return contract.addKnotToOpenIndexAndWithdraw(
        stakehouses,
        memberIDs,
        recipient
    );

};

const _batchTransferKnotsToSingleIndex = async (signer, stakehouses, memberIDs, targetSavETHIndexID) => {

    for(let i = 0; i < memberIDs.length; i++) {
        memberIDs[i] = _add0x(memberIDs[i]);
    }

    const contract = (await getContractInstance(signer)).savETHBatch();

    return contract.transferKnotsToSingleIndex(
        stakehouses,
        memberIDs,
        targetSavETHIndexID
    );
};

const _BLSAuthentication = async (signer, keystorePassword, keystore, depositData, depositorSignaturePayload) => {

    const chainID = await _extractChainID(signer);
    const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    try {
        const blsAuthResponse = await axios.post(stakehouseUrls.BLS_AUTH_ENDPOINT, {
            keystore_password: keystorePassword,
            keystore,
            deposit_data: depositData,
            depositor_signature_payload: depositorSignaturePayload
        });
        return blsAuthResponse.data;
    } catch(e) {
        return e.response.data;
    }
};

const _batchBLSAuthentication = async (signer, keystorePassword, keystores, depositData, depositorSignaturePayload) => {

    const chainID = await _extractChainID(signer);
    const { stakehouseUrls } = _getChainSpecificConstants(chainID);

    try {
        const blsAuthResponse = await axios.post(stakehouseUrls.BATCH_BLS_AUTH_ENDPOINT, {
            keystore_password: keystorePassword,
            keystores,
            deposit_data: depositData,
            depositor_signature_payload: depositorSignaturePayload
        });
        const data = blsAuthResponse.data;
        let res = [];

        for(let i=0; i<data.responses.length; ++i) {
            res.push(JSON.parse(data.responses[i].body));
        }

        return res;

    } catch(e) {
        return e.response.data;
    }
};

const _rageQuitKnot = async (signer, stakehouseAddress, balanceReport, topUpAmountInWei) => {

    balanceReport.report.blsPublicKey = _add0x(balanceReport.report.blsPublicKey);
    balanceReport.report.withdrawalCredentials = _add0x(balanceReport.report.withdrawalCredentials);

    const contract =  (await getContractInstance(signer)).transactionRouter();

    const signature = {
        deadline: balanceReport.deadline,
        v: balanceReport.v,
        r: Buffer.from(balanceReport.r, 'hex'),
        s: Buffer.from(balanceReport.s, 'hex'),
    };

    return contract.rageQuitKnot(
        stakehouseAddress,
        balanceReport.report.blsPublicKey,
        balanceReport.report,
        signature,
        { value: topUpAmountInWei }
    );
};

const _rageQuit = async (signer, stakehouseAddress, userAddress, balanceReport) => {

    await _rageQuitChecks(
        signer, 
        stakehouseAddress, 
        userAddress, 
        balanceReport
    );

    const topUp = await _calculateExitFee(signer, balanceReport);

    return _rageQuitKnot(
        signer,
        stakehouseAddress,
        balanceReport,
        topUp
    );
};

const _depositIntoSavETHRegistry = async(signer, savETHRecipient, amount) => {

    const contract =  (await getContractInstance(signer)).savETHManager();

    return contract.deposit(
        savETHRecipient,
        amount
    );
};

const _depositAndIsolateKnotIntoIndex = async(signer, stakehouseAddress, blsPublicKey, indexID) => {

    blsPublicKey = _add0x(blsPublicKey);

    const contract =  (await getContractInstance(signer)).savETHManager();

    return contract.depositAndIsolateKnotIntoIndex(
        stakehouseAddress,
        blsPublicKey,
        indexID
    );
};

const _slash = async(signer, stakehouseAddress, authenticatedReport) => {

    authenticatedReport.report.blsPublicKey = _add0x(authenticatedReport.report.blsPublicKey);
    authenticatedReport.report.withdrawalCredentials = _add0x(authenticatedReport.report.withdrawalCredentials);

    const transactionRouterContract =  (await getContractInstance(signer)).transactionRouter();

    const accountManagerContract =  (await getContractInstance(signer)).accountManager();

	if (authenticatedReport && authenticatedReport.report) {
		const currentAdjustedActiveBalanceBN = ethers.BigNumber.from(authenticatedReport.report.activeBalance);
		const lastKnownAdjustedActiveBalance = await accountManagerContract.getLastKnownActiveBalance(_add0x(authenticatedReport.report.blsPublicKey)); // gwei

        if (currentAdjustedActiveBalanceBN.gt(lastKnownAdjustedActiveBalance)) {
            throw customErrors.CANNOT_BE_SLASHED;
        }
    }

    const signature = {
        deadline: authenticatedReport.deadline,
        v: authenticatedReport.v,
        r: Buffer.from(authenticatedReport.r, 'hex'),
        s: Buffer.from(authenticatedReport.s, 'hex'),
    };

    return transactionRouterContract.slash(
        stakehouseAddress,
        authenticatedReport.report.blsPublicKey,
        authenticatedReport.report,
        signature
    );
};

const _slashAndTopUpSlot = async(signer, stakehouseAddress, slasherAddress, buyAmount, ethValue, authenticatedReport) => {

    authenticatedReport.report.blsPublicKey = _add0x(authenticatedReport.report.blsPublicKey);
    authenticatedReport.report.withdrawalCredentials = _add0x(authenticatedReport.report.withdrawalCredentials);

    const transactionRouterContract =  (await getContractInstance(signer)).transactionRouter();

    const accountManagerContract =  (await getContractInstance(signer)).accountManager();

	if (authenticatedReport && authenticatedReport.report) {
		const currentAdjustedActiveBalanceBN = ethers.BigNumber.from(authenticatedReport.report.activeBalance);
		const lastKnownAdjustedActiveBalance = await accountManagerContract.getLastKnownActiveBalance(_add0x(authenticatedReport.report.blsPublicKey)); // gwei

        if (currentAdjustedActiveBalanceBN.gt(lastKnownAdjustedActiveBalance)) {
            throw customErrors.CANNOT_BE_SLASHED;
        }
    }

    const signature = {
        deadline: authenticatedReport.deadline,
        v: authenticatedReport.v,
        r: Buffer.from(authenticatedReport.r, 'hex'),
        s: Buffer.from(authenticatedReport.s, 'hex'),
    };

    return transactionRouterContract.slashAndTopUpSlot(
        stakehouseAddress,
        authenticatedReport.report.blsPublicKey,
        slasherAddress,
        buyAmount,
        authenticatedReport.report,
        signature,
        { value: ethValue }
    );
};

const _createIndex = async (signer, ownerAddress) => {

    const contract =  (await getContractInstance(signer)).savETHManager();

    return contract.createIndex(
        ownerAddress
    );
};

const _approveTransferOfIndexOwnership = async (signer, indexID, spender) => {

    const contract =  (await getContractInstance(signer)).savETHManager();

    return contract.approveForIndexOwnershipTransfer(
        indexID,
        spender
    );
};

const _transferIndexOwnership = async (signer, indexID, recipient) => {

    const contract =  (await getContractInstance(signer)).savETHManager();

    return contract.transferIndexOwnership(
        indexID,
        recipient
    );
};

const _transferKnotToAnotherIndex = async (signer, stakehouseAddress, blsPublicKey, newIndexID) => {

    const status = await _getValidatorLifecycleStatus(signer, blsPublicKey);
    if(status !== lifecycleStatuses.TOKENS_MINTED) {
        throw customErrors.STATUS_NOT_3;
    }

    blsPublicKey = _add0x(blsPublicKey);

    const contract =  (await getContractInstance(signer)).savETHManager();
    
    return contract.transferKnotToAnotherIndex(
        stakehouseAddress,
        blsPublicKey,
        newIndexID
    );
};

const _approveSpendingOfKnotInIndex = async (signer, stakehouseAddress, blsPublicKey, spender) => {

    const status = await _getValidatorLifecycleStatus(signer, blsPublicKey);
    if(status !== lifecycleStatuses.TOKENS_MINTED) {
        throw customErrors.STATUS_NOT_3;
    }
    
    blsPublicKey = _add0x(blsPublicKey);

    const contract =  (await getContractInstance(signer)).savETHManager();

    return contract.approveSpendingOfKnotInIndex(
        stakehouseAddress,
        blsPublicKey,
        spender
    );
};

const _joinStakeHouseAndCreateBrand = async (
    signer, 
    userAddress, 
    brandTicker, 
    stakehouseAddress, 
    savETHIndexId, 
    authenticatedReport) => {

    const status = await _getValidatorLifecycleStatus(signer, authenticatedReport.report.blsPublicKey);
    if(status !== lifecycleStatuses.DEPOSIT_COMPLETED) {
        throw customErrors.STATUS_NOT_2;
    }

    const activeBalance = ethers.BigNumber.from(authenticatedReport.report.activeBalance);
    const threshold = ethers.BigNumber.from('32000000000');

    if(activeBalance.lt(threshold)) { 
        throw customErrors.ETH_BALANCE_LT_32;
    }

    userAddress = _add0x(userAddress);
    stakehouseAddress = _add0x(stakehouseAddress);
    authenticatedReport.report.blsPublicKey = _add0x(authenticatedReport.report.blsPublicKey);
    authenticatedReport.report.withdrawalCredentials = _add0x(authenticatedReport.report.withdrawalCredentials);

    const contract =  (await getContractInstance(signer)).transactionRouter();

    const signature = {
        deadline: authenticatedReport.deadline,
        v: authenticatedReport.v,
        r: Buffer.from(authenticatedReport.r, 'hex'),
        s: Buffer.from(authenticatedReport.s, 'hex'),
    };

    return contract.joinStakeHouseAndCreateBrand(
        userAddress,
        authenticatedReport.report.blsPublicKey,
        brandTicker,
        stakehouseAddress,
        savETHIndexId,
        authenticatedReport.report,
        signature
    );
};

const _authorizeRepresentative = async (signer, representative, enable) => {

    if(_add0x(representative) === ethers.constants.AddressZero) {
        throw customErrors.ZERO_ADDRESS;
    }

    const contract = (await getContractInstance(signer)).transactionRouter();

    return contract.authorizeRepresentative(
        representative,
        enable
    );
};

const _isRepresentativeAuthorized = async (signer, representative, representee) => {

    const contract = (await getContractInstance(signer)).transactionRouter();

    return contract.userToRepresentativeStatus(
        representee,
        representative
    );
};

const _deposit = async (signer, keystore, password, depositData, privateKey=null, user=null) => {

    const userAddress = user ? _add0x(user) : await signer.getAddress();
    const blsPublicKey = _add0x(depositData[0].pubkey);
    const blsSignature = _add0x(depositData[0].signature);

    const validatorLifeCycle = await _getValidatorLifecycleStatus(signer, blsPublicKey);

    if(validatorLifeCycle >= 2) {
        throw customErrors.BLS_KEY_DEPOSITED;
    }

    if(validatorLifeCycle === 0) {

        console.log("Registering the validator's initials...");
        let tx = await _registerValidatorInitials(
            signer,
            userAddress,
            blsPublicKey,
            blsSignature
        );
        console.log("Register validator initials transaction hash: ", tx.hash);
        await tx.wait();
    }

    console.log("Authenticating the user credentials with the deposit router...");

    let signaturePayload;
    if(privateKey) {
        signaturePayload = await _getPersonalSignInitialsByPrivateKey(
            depositData[0].pubkey,
            depositData[0].signature,
            _remove0x(privateKey)
        );
    }
    else {
        try {
            const provider = signer.provider;
            signaturePayload = await _getPersonalSignInitials(
                provider,
                blsPublicKey,
                blsSignature,
                userAddress
            );
        } catch(e) {
            throw customErrors.INVALID_SERVER;
        }
    }

    const authenticationPackage = await _BLSAuthentication(
        signer,
        password, 
        keystore, 
        depositData, 
        signaturePayload
    );
    console.log("Authentication package: ", authenticationPackage);

    if(!user) {
        console.log("Registering the validator...");
        const registerValidatorTx = await _registerValidator(
            signer,
            userAddress,
            authenticationPackage
        );
        console.log("Register validator transaction hash: ", registerValidatorTx.hash);
    }
    else {
        const { deposit_data, cipher_text, encryptor_aes_key, deadline, v, r, s } = authenticationPackage;
        const signature = { deadline, v, r: _add0x(r), s: _add0x(s) };

        const inputParams = [
            _add0x(userAddress),
            _add0x(deposit_data[0].pubkey),
            _add0x(cipher_text),
            _add0x(encryptor_aes_key),
            signature,
            _add0x(deposit_data[0].deposit_data_root)
        ];

        console.log("Generating encoded ABI to register the validator...");

        const txRouterInterface = new ethers.utils.Interface(stakehouseContracts.TRANSACTION_ROUTER);
        const encodedData = txRouterInterface.encodeFunctionData(
            "registerValidator",
            inputParams
        );
        console.log("Register validator encoded data: ", encodedData);

        return encodedData;
    }
};

module.exports = {
    _reportBalanceIncrease, 
    _reportBalance,
    _registerValidatorInitials, 
    _registerValidator, 
    _createStakehouse, 
    _joinStakehouse, 
    _addKnotToOpenIndexAndWithdraw, 
    _batchAddKnotToOpenIndexAndWithdraw,
    _batchTransferKnotsToSingleIndex,
    _BLSAuthentication,
    _batchBLSAuthentication,
    _rageQuitKnot,
    _rageQuit,
    _depositIntoSavETHRegistry,
    _depositAndIsolateKnotIntoIndex,
    _slash,
    _slashAndTopUpSlot,
    _createIndex,
    _approveTransferOfIndexOwnership,
    _transferIndexOwnership,
    _transferKnotToAnotherIndex,
    _approveSpendingOfKnotInIndex,
    _joinStakeHouseAndCreateBrand,
    _authorizeRepresentative,
    _isRepresentativeAuthorized,
    _deposit
};
